import { createContext, ReactElement, ReactNode, useContext } from 'react'
import { cn } from 'app/lib/utils'

/**
 * `inset` inside the settings well: the well already sits in the main area, so a page's own
 * 40px gutter would push its header off the rail's line. 16px on a phone either way.
 */
export const PageGutterContext = createContext<`page` | `inset`>(`page`)

/** Horizontal page gutter by context: 16px on a phone, then 40px on a page or 24px inside the settings well. */
export const PAGE_GUTTER: Record<`page` | `inset`, string> = { page: `px-4 md:px-10`, inset: `px-4 md:px-6` }

export type PageScrollProps = {
  /**
   * Fixed header content (title, tabs, controls). Rendered in the page's gutter (40px, or 24px
   * inside the settings well) above the scroll region and stays pinned while the body scrolls.
   * Omit for pages with no header.
   */
  header?: ReactNode
  /** Scrollable page content. */
  children: ReactNode
  /** Extra classes for the scrollable content region (e.g. `pb-14`, spacing). */
  contentClassName?: string
  /** Extra classes for the outer wrapper. */
  className?: string
  /**
   * 'page' (default): the wrapper owns a page-level `overflow-y-auto` and pads
   * content symmetrically (`px-10`); the scrollbar sits at the window's right
   * edge. Use for dashboards, forms, settings — normal page-scroll content.
   *
   * 'table': the child owns its own scroll (a virtualized `DataTable`). The
   * wrapper restores the LEFT gutter (`pl-10`) and leaves a small right margin
   * (`pr-1`) so the table content uses the full page width while its vertical
   * scrollbar sits just beyond the content, a hair in from the window edge.
   * Pair with `edgePadded` on the table so its toolbar's right controls stay off
   * the edge.
   */
  variant?: 'page' | 'table'
}

/**
 * Full-bleed vertical page scroll: the scrollbar sits at the window's right
 * edge while content stays padded inside the standard gutter.
 *
 * Requires the route to opt into `EDGE_SCROLL_PAGES` in `AppShell`, which strips
 * `main`'s `px-10 pt-8`. Without that opt-in, `main`'s gutter insets the
 * scrollbar ~40px as usual.
 */
export function PageScroll({
  header,
  children,
  contentClassName,
  className,
  variant = `page`,
}: PageScrollProps): ReactElement {
  const isTable = variant === `table`
  const gutter = PAGE_GUTTER[useContext(PageGutterContext)]
  return (
    <div className={cn(`flex h-full flex-col`, className)}>
      {/* 16px on a phone, 40px from md up (24px inside the settings well): the gutter main
          itself uses on pages that do not opt into this component. A fixed 40px cost every
          edge-scroll page 48px of a 375px screen. */}
      {header != null && <div className={cn(`shrink-0 pt-8`, gutter)}>{header}</div>}
      <div
        className={cn(
          isTable
            ? // The table carries its own frame now, so the card's border (not
              // its content) lines up with the page's gutter and the
              // scrollbars sit inside the card. `pb-6` lets the frame's bottom
              // edge close on screen instead of running off the window.
              cn(`flex min-h-0 w-full grow flex-col overflow-hidden pb-6`, gutter)
            : cn(`min-h-0 flex-1 overflow-y-auto`, gutter),
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}
